import React, {useEffect, useState, useRef} from 'react';
import {extendTheme} from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import TableChartIcon from '@mui/icons-material/TableChart';
import PeopleIcon from '@mui/icons-material/People';
import TimelineIcon from '@mui/icons-material/Timeline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import {AppProvider} from '@toolpad/core/AppProvider';
import {DashboardLayout} from '@toolpad/core/DashboardLayout';
import {Routes, Route, useNavigate, useLocation} from 'react-router-dom';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Home from './components/Home';
import ProductTable from './components/ProductTable';
import CustomerTable from './components/CustomerTable';
import CustomerForecast from './components/CustomerForecast';
import ForecastOutputsSummary from './components/ForecastOutputsSummary';
import ForecastOutputsDemand from './components/ForecastOutputsDemand';
import ForecastOutputsPromotions from './components/ForecastOutputsPromotions';
import ForecastOutputsSales from './components/ForecastOutputsSales.jsx';
import ForecastOutputsAccount from './components/ForecastOutputsAccount';
import ForecastAccuracy from './components/ForecastAccuracy';
import ProfilePage from './components/ProfilePage';
import ThirdPartyIntegrationsPage from './components/ThirdPartyIntegrationsPage';
import SynchronizationPage from './components/SynchronizationPage';
import SettingsPage from './components/SettingsPage';
import AdminPage from './components/AdminPage';
import ImportPage from './components/ImportPage';
import ProtectedRoute from './ProtectedRoute';
import {AuthProvider, useAuth} from './AuthContext';
import Typography from '@mui/material/Typography';
import axiosInstance from "./AxiosInstance";
import {CardGiftcard, Layers, Person2} from "@mui/icons-material";
import SyncIcon from '@mui/icons-material/Sync';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Box from "@mui/material/Box";
import usePageTracking from './hooks/usePageTracking';
import ReactGA from 'react-ga4';
import CustomToolbarActions from './components/CustomToolbarActions';

const NAVIGATION = [
    {
        kind: 'header',
        title: 'Main items',
    },
    {
        segment: '',
        title: 'Dashboard',
        icon: <DashboardIcon/>,
    },
    {
        segment: 'products-table',
        title: 'Products',
        icon: <CardGiftcard/>,
    },
    {
        segment: 'customers-table',
        title: 'Customers',
        icon: <PeopleIcon/>,
    },
    {
        segment: 'customer-forecast',
        title: 'Forecasts',
        icon: <TimelineIcon/>,
    },
    {
        kind: 'divider',
    },
    {
        kind: 'header',
        title: 'Analytics',
    },
    {
        segment: 'outputs',
        title: 'Outputs',
        icon: <BarChartIcon/>,
        children: [
            {
                segment: 'forecast-outputs',
                title: 'Summary',
                icon: <TableChartIcon/>,
            },
            {
                segment: 'forecast-outputs-account',
                title: 'Account',
                icon: <TableChartIcon/>,
            },
            {
                segment: 'forecast-outputs-demand',
                title: 'Demand',
                icon: <TableChartIcon/>,
            },
            {
                segment: 'forecast-accuracy',
                title: 'Forecast Accuracy',
                icon: <TableChartIcon/>,
            },
            {
                segment: 'forecast-outputs-promotions',
                title: 'Promotions',
                icon: <TableChartIcon/>,
            },
            {
                segment: 'forecast-outputs-sales',
                title: 'Sales',
                icon: <TableChartIcon/>,
            },
        ],
    },
    {
        kind: 'divider',
    },
    {
        kind: 'header',
        title: 'Options',
    },
    {
        segment: 'settings',
        title: 'Settings',
        icon: <SettingsIcon/>,
        children: [
            {
                segment: 'profile',
                title: 'Profile',
                icon: <Person2/>,
            },
            {
                segment: 'import',
                title: 'Import',
                icon: <CloudUploadIcon/>,
            },
            {
                segment: 'integrations',
                title: 'Integrations',
                icon: <Layers/>,
            },
            {
                segment: 'synchronization',
                title: 'Sync',
                icon: <SyncIcon/>,
            },
        ]
    },
];

const theme = extendTheme({
    colorSchemes: {light: true, dark: true},
    colorSchemeSelector: 'class',
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 600,
            lg: 1200,
            xl: 1536,
        },
    },
    components: {
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
                        margin: 0,
                        alignSelf: 'center',
                        lineHeight: 1,
                    },
                    '& .MuiTablePagination-toolbar': {
                        alignItems: 'center',
                    },
                },
            },
        },
    },
});

function useCustomRouter() {
    const navigate = useNavigate();
    const location = useLocation();

    return React.useMemo(() => ({
        pathname: location.pathname,
        searchParams: new URLSearchParams(location.search),
        navigate: (path) => navigate(path),
    }), [location, navigate]);
}

function SidebarFooter({mini}) {
    const [versionInfo, setVersionInfo] = useState({version: '', buildNumber: ''});

    useEffect(() => {
        axiosInstance.get('/info')
            .then(response => {
                setVersionInfo({
                    version: response.data.version,
                    buildNumber: response.data.buildNumber,
                });
            })
            .catch(error => {
                console.error('Error fetching version info:', error);
            });
    }, []);

    return (
        <Box
            sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 1,
                textAlign: 'center',
            }}
        >
            <Typography
                variant="caption"
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
                {mini
                    ? '© AIQ'
                    : `© ${new Date().getFullYear()} AbakusIQ Ltd | v${versionInfo.version}.${versionInfo.buildNumber}`}
            </Typography>
        </Box>
    );
}

function AppWithNavigation() {

    usePageTracking();

    const router = useCustomRouter();
    const navigate = useNavigate();
    const {isAuthenticated, handleLogout, organisation, email} = useAuth();
    const toolbarActionsRef = useRef();

    const [session, setSession] = React.useState({
        user: {
            name: organisation || '',
            email: email || '',
        },
    });

    const [featureFlags, setFeatureFlags] = useState({
        enableThirdPartyIntegration: false,
    });

    const [navigation, setNavigation] = useState([]);

    useEffect(() => {
        if (organisation && email) {
            setSession({
                user: {
                    name: organisation,
                    email: email,
                },
            });
        }
    }, [organisation, email]);

    useEffect(() => {
        if (!isAuthenticated) return;

        axiosInstance.get('/info/features')
            .then(response => {
                const { enableThirdPartyIntegration } = response.data;

                const nav = NAVIGATION.map(item => {
                    if (item.segment === 'settings' || item.segment === 'outputs') {
                        const filteredChildren = item.children?.filter(child => {
                            if ((child.segment === 'forecast-outputs-sales' ||  child.segment === 'integrations' || child.segment === 'synchronization') && !enableThirdPartyIntegration) {
                                return false;
                            }
                            return true;
                        });
                        return { ...item, children: filteredChildren };
                    }
                    return item;
                });

                setFeatureFlags({ enableThirdPartyIntegration });
                setNavigation(nav);
            })
            .catch(error => {
                console.warn('Feature flag check failed:', error);
                setNavigation(NAVIGATION); // fallback
            });
    }, [isAuthenticated]);

    const authentication = React.useMemo(() => {
        return {
            signIn: () => {},
            signOut: () => {
                handleLogout();
                setSession(null);
                navigate('/login');
            },
        };
    }, [handleLogout, navigate]);

    const ToolbarActionsWithAuth = React.useMemo(() => {
        return React.forwardRef((props, ref) => 
            <CustomToolbarActions 
                ref={ref} 
                isAuthenticated={isAuthenticated} 
                enableThirdPartyIntegration={featureFlags.enableThirdPartyIntegration}
            />
        );
    }, [isAuthenticated, featureFlags.enableThirdPartyIntegration]);

    const refreshNotifications = React.useCallback(() => {
        if (toolbarActionsRef.current) {
            toolbarActionsRef.current.refreshNotifications();
        }
    }, []);

    React.useEffect(() => {
        window.refreshNotifications = refreshNotifications;
        return () => {
            delete window.refreshNotifications;
        };
    }, [refreshNotifications]);

    return (
        <AppProvider
            session={session}
            router={router}
            authentication={authentication}
            navigation={navigation}
            branding={{title: 'AbakusIQ'}}
            theme={theme}
        >
            <DashboardLayout
                hideNavigation={!isAuthenticated}
                slots={{
                    sidebarFooter: SidebarFooter,
                    toolbarActions: (props) => <ToolbarActionsWithAuth ref={toolbarActionsRef} {...props} />,
                }}
            >
                <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<LoginPage/>}/>
                    <Route path="/forgot-password" element={<ForgotPassword/>}/>
                    <Route path="/reset-password" element={<ResetPassword/>}/>

                    {/* Protected Routes */}
                    <Route element={<ProtectedRoute/>}>
                        <Route path="/" element={<Home/>}/>
                        <Route path="/products-table" element={<ProductTable/>}/>
                        <Route path="/customers-table" element={<CustomerTable/>}/>
                        <Route path="/customer-forecast/:id?" element={<CustomerForecast/>}/>
                        <Route path="/outputs/forecast-outputs" element={<ForecastOutputsSummary/>}/>
                        <Route path="/outputs/forecast-outputs-account" element={<ForecastOutputsAccount/>}/>
                        <Route path="/outputs/forecast-outputs-demand" element={<ForecastOutputsDemand/>}/>
                        <Route path="/outputs/forecast-outputs-promotions" element={<ForecastOutputsPromotions/>}/>
                        <Route path="/outputs/forecast-accuracy" element={<ForecastAccuracy/>}/>
                        <Route path="/settings/profile" element={<ProfilePage/>}/>
                        <Route path="/settings/import" element={<ImportPage/>}/>
                        {featureFlags.enableThirdPartyIntegration && (
                            <>
                                <Route path="/outputs/forecast-outputs-sales" element={<ForecastOutputsSales/>}/>
                                <Route path="/settings/integrations" element={<ThirdPartyIntegrationsPage/>}/>
                                <Route path="/settings/synchronization" element={<SynchronizationPage/>}/>
                            </>
                        )}
                    </Route>
                    {/* Admin Routes */}
                    <Route element={<ProtectedRoute isAdminRequired={true}/>}>
                        <Route path="/admin" element={<AdminPage/>}/>
                        <Route path="/register" element={<RegisterPage/>}/>
                    </Route>
                </Routes>
            </DashboardLayout>
        </AppProvider>
    );
}

function App() {

    useEffect(() => {
        const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

        if (window.location.hostname === 'abakusiq.com' && gaMeasurementId) {
            ReactGA.initialize(gaMeasurementId);
        }
    }, []);

    return (
        <AuthProvider>
            <Routes>
                <Route path="*" element={<AppWithNavigation/>}/>
            </Routes>
        </AuthProvider>
    );
}

export default App;

